import dayjs from "dayjs";
import { ChevronDown, ChevronUp, Loader2, MessageCircle, SendHorizontal } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { trpc } from "~/api/trpc";
import { Button } from "~/components/ui/button";
import { Textarea } from "~/components/ui/textarea";
import { cn } from "~/lib/utils";
import { useUserStore } from "~/stores/user-store";

const MAX_MESSAGE_LENGTH = 2000;
const COLLAPSED_MESSAGE_COUNT = 3;

export type AppointmentChatMessage = {
    id: string;
    message: string;
    createdAt: string | Date;
    isOwn: boolean;
    author: {
        id: number;
        firstName: string;
        lastName: string;
    };
};

export const useAppointmentChat = (appointmentId: string, enabled: boolean) => {
    const trpcUtils = trpc.useUtils();
    const lastMarkedRef = useRef<string | null>(null);
    const isEnabled = enabled && !!appointmentId;

    const messagesQuery = trpc.appointmentChat.getMessages.useQuery(
        { appointmentId },
        {
            enabled: isEnabled,
            refetchInterval: isEnabled ? 5 * 1000 : false,
            retry: false,
        }
    );
    const messages: AppointmentChatMessage[] = messagesQuery.data ?? [];
    const newestMessageCreatedAt = messages.length > 0 ? String(messages[messages.length - 1].createdAt) : null;

    const markAsReadMutation = trpc.appointmentChat.markAsRead.useMutation({
        onSuccess: () => {
            trpcUtils.appointmentChat.getUnreadCounts.invalidate();
        },
    });

    const sendMessageMutation = trpc.appointmentChat.sendMessage.useMutation({
        onSuccess: (_data, variables) => {
            trpcUtils.appointmentChat.getMessages.invalidate({ appointmentId: variables.appointmentId });
            trpcUtils.appointmentChat.getUnreadCounts.invalidate();
        },
        onError: (error) => {
            toast.error("Nachricht konnte nicht gesendet werden.", {
                description: error.message,
            });
        },
    });

    useEffect(() => {
        if (!isEnabled) {
            lastMarkedRef.current = null;
        }
    }, [isEnabled]);

    useEffect(() => {
        if (!isEnabled || !newestMessageCreatedAt) {
            return;
        }
        const markKey = `${appointmentId}:${newestMessageCreatedAt}`;
        if (lastMarkedRef.current === markKey) {
            return;
        }
        lastMarkedRef.current = markKey;
        markAsReadMutation.mutate({
            appointmentId,
            lastSeenMessageCreatedAt: new Date(newestMessageCreatedAt),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEnabled, appointmentId, newestMessageCreatedAt]);

    const send = (message: string, onSuccess?: () => void) => {
        const trimmed = message.trim();
        if (!trimmed || sendMessageMutation.isPending || !appointmentId) {
            return;
        }
        sendMessageMutation.mutate({ appointmentId, message: trimmed }, { onSuccess });
    };

    return {
        messages,
        isLoading: messagesQuery.isLoading,
        isError: messagesQuery.isError,
        send,
        isSending: sendMessageMutation.isPending,
    };
};

const formatMessageTime = (createdAt: string | Date, timezone: string) => {
    const time = dayjs(createdAt).tz(timezone);
    const now = dayjs().tz(timezone);
    return time.isSame(now, 'day') ? time.format('HH:mm') : time.format('DD.MM.YYYY HH:mm');
};

type AppointmentChatMessageListProps = {
    messages: AppointmentChatMessage[];
    isLoading?: boolean;
    compact?: boolean;
    className?: string;
};

export const AppointmentChatMessageList = ({ messages, isLoading, compact, className }: AppointmentChatMessageListProps) => {
    const timezone = useUserStore((state) => state.user?.timezone || 'Europe/Berlin');
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (container) {
            container.scrollTop = container.scrollHeight;
        }
    }, [messages.length]);

    return (
        <div
            ref={scrollContainerRef}
            className={cn("overflow-y-auto flex flex-col", compact ? "gap-2" : "gap-3", className)}
        >
            {isLoading ? (
                <div className="flex flex-1 items-center justify-center py-2 text-muted-foreground">
                    <Loader2 className={cn("animate-spin", compact ? "h-4 w-4" : "h-5 w-5")} />
                </div>
            ) : messages.length === 0 ? (
                <div className={cn("flex flex-1 items-center justify-center text-center text-muted-foreground", compact ? "text-xs py-1" : "text-sm px-6")}>
                    Teile hier Infos zum Termin mit dem Team.
                </div>
            ) : (
                messages.map((message) => (
                    <div
                        key={message.id}
                        className={cn("flex flex-col max-w-[85%]", message.isOwn ? "self-end items-end" : "self-start items-start")}
                    >
                        <div className="flex items-baseline gap-2 px-1 text-xs text-muted-foreground">
                            <span className="font-medium text-gray-700">
                                {message.isOwn ? 'Du' : `${message.author.firstName} ${message.author.lastName}`}
                            </span>
                            <span>{formatMessageTime(message.createdAt, timezone)}</span>
                        </div>
                        <div
                            className={cn(
                                "mt-0.5 rounded-lg whitespace-pre-wrap break-words shadow-xs",
                                compact ? "px-2.5 py-1.5 text-xs" : "px-3 py-2 text-sm",
                                message.isOwn ? "bg-primary-500 text-white" : "bg-white border"
                            )}
                        >
                            {message.message}
                        </div>
                    </div>
                ))
            )}
        </div>
    );
};

type AppointmentChatComposerProps = {
    onSend: (message: string, onSuccess: () => void) => void;
    isSending: boolean;
    autoFocus?: boolean;
    compact?: boolean;
};

export const AppointmentChatComposer = ({ onSend, isSending, autoFocus, compact }: AppointmentChatComposerProps) => {
    const [draft, setDraft] = useState('');

    const handleSend = () => {
        onSend(draft, () => setDraft(''));
    };

    return (
        <div className="flex items-end gap-2">
            <Textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                        e.preventDefault();
                        handleSend();
                    }
                }}
                placeholder="Nachricht schreiben..."
                maxLength={MAX_MESSAGE_LENGTH}
                className={cn("resize-none bg-white", compact ? "min-h-9 max-h-32 text-sm md:text-xs" : "min-h-11 max-h-40")}
                autoFocus={autoFocus}
            />
            <Button
                size="icon"
                className={cn("shrink-0", compact ? "h-9 w-9" : "h-11 w-11")}
                onClick={handleSend}
                disabled={!draft.trim() || isSending}
            >
                {isSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <SendHorizontal className="h-4 w-4" />}
            </Button>
        </div>
    );
};

type AppointmentChatPanelProps = {
    appointmentId: string;
};

export const AppointmentChatPanel = ({ appointmentId }: AppointmentChatPanelProps) => {
    const { messages, isLoading, isError, send, isSending } = useAppointmentChat(appointmentId, true);
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        setExpanded(false);
    }, [appointmentId]);

    if (isError) {
        return null;
    }

    const hiddenCount = Math.max(0, messages.length - COLLAPSED_MESSAGE_COUNT);
    const visibleMessages = expanded ? messages : messages.slice(-COLLAPSED_MESSAGE_COUNT);

    return (
        <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-semibold flex items-center gap-1.5">
                    <MessageCircle className="h-4 w-4" />
                    Termin-Chat
                    {messages.length > 0 && (
                        <span className="text-xs font-normal text-muted-foreground">
                            ({messages.length} {messages.length === 1 ? 'Nachricht' : 'Nachrichten'})
                        </span>
                    )}
                </p>
                {hiddenCount > 0 && (
                    <Button
                        variant="link"
                        size="sm"
                        className="h-auto p-0 text-xs"
                        onClick={() => setExpanded((prev) => !prev)}
                    >
                        {expanded ? (
                            <>
                                <ChevronUp className="h-3.5 w-3.5 mr-1" />
                                Weniger anzeigen
                            </>
                        ) : (
                            <>
                                <ChevronDown className="h-3.5 w-3.5 mr-1" />
                                Alle {messages.length} Nachrichten anzeigen
                            </>
                        )}
                    </Button>
                )}
            </div>

            <AppointmentChatMessageList
                key={expanded ? 'all' : 'recent'}
                messages={visibleMessages}
                isLoading={isLoading}
                compact
                className={cn(expanded && "max-h-80 pr-1")}
            />

            <AppointmentChatComposer onSend={send} isSending={isSending} compact />
        </div>
    );
};
